/**
 * @module ui/pages/runs/test-runs/TestRunsFilters
 *
 * The "More filters" dialog for the test-runs toolbar (currently just the
 * date range — Target / Status / Run by are fixed-size inline menus in
 * `TestRunsFilterBar.tsx`), plus the active-filter chip list shared by the
 * toolbar. Mirrors `IssuesFilters.tsx`.
 */
import React from 'react';
import {
  Button,
  DateRangePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  MoreFiltersButton,
  type FilterToolbarChip,
} from '@chatsdet/ui';
import type { DateRangeValue } from '../../../../utils/dateRange';
import { formatDateRangeDisplay } from '../../../../utils/dateRange';
import type { TestRunsSearch } from '../runsRouteSearch';

export const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  completed_with_errors: 'Completed (errors)',
  completed_with_inconclusive: 'Completed (inconclusive)',
  canceled: 'Canceled',
  timeout: 'Timed out',
};

type TestRunsFiltersProps = {
  targetFilter: string[];
  statusFilter: string[];
  triggeredByFilter: string[];
  dateRange: DateRangeValue;

  targetLabelMap: Record<string, string>;
  triggeredByLabelMap: Record<string, string>;

  onFilterChange: (updates: Partial<TestRunsSearch>) => void;
  onDateRangeChange: (range: DateRangeValue) => void;
  onClearDateRange: () => void;

  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function TestRunsFilters({
  targetFilter,
  statusFilter,
  triggeredByFilter,
  dateRange,
  targetLabelMap,
  triggeredByLabelMap,
  onFilterChange,
  onDateRangeChange,
  onClearDateRange,
  isOpen,
  onOpenChange,
}: TestRunsFiltersProps) {
  const isDateRangeInvalid = React.useMemo(() => {
    if (!dateRange?.start || !dateRange?.end) return false;
    return dateRange.start.compare(dateRange.end) > 0;
  }, [dateRange]);

  const dateLabel = formatDateRangeDisplay(dateRange);

  const activeFilterChips: FilterToolbarChip[] = React.useMemo(() => {
    const chips: FilterToolbarChip[] = [];
    targetFilter.forEach((key) => {
      chips.push({ id: `target:${key}`, label: `Target: ${targetLabelMap[key] ?? key}` });
    });
    statusFilter.forEach((status) => {
      chips.push({ id: `status:${status}`, label: `Status: ${STATUS_LABELS[status] ?? status}` });
    });
    triggeredByFilter.forEach((id) => {
      const label = id === 'system' ? 'System' : (triggeredByLabelMap[id] ?? id);
      chips.push({ id: `triggeredBy:${id}`, label: `Run by: ${label}` });
    });
    if (dateLabel) {
      chips.push({ id: 'dateRange', label: `Started: ${dateLabel}` });
    }
    return chips;
  }, [dateLabel, statusFilter, targetFilter, targetLabelMap, triggeredByFilter, triggeredByLabelMap]);

  const hasActiveFilters = activeFilterChips.length > 0;

  const handleFilterRemove = React.useCallback(
    (chipId: string) => {
      if (chipId === 'dateRange') {
        onClearDateRange();
        return;
      }
      if (chipId.startsWith('target:')) {
        const key = chipId.slice('target:'.length);
        const next = targetFilter.filter((t) => t !== key);
        onFilterChange({ targets: next.length > 0 ? next : undefined, page: 1 });
        return;
      }
      if (chipId.startsWith('status:')) {
        const status = chipId.slice('status:'.length);
        const next = statusFilter.filter((s) => s !== status);
        onFilterChange({ statuses: next.length > 0 ? next : undefined, page: 1 });
        return;
      }
      if (chipId.startsWith('triggeredBy:')) {
        const id = chipId.slice('triggeredBy:'.length);
        const next = triggeredByFilter.filter((t) => t !== id);
        onFilterChange({ triggeredBy: next.length > 0 ? next : undefined, page: 1 });
      }
    },
    [onClearDateRange, onFilterChange, statusFilter, targetFilter, triggeredByFilter]
  );

  const handleResetFilters = React.useCallback(() => {
    onFilterChange({
      targets: undefined,
      statuses: undefined,
      triggeredBy: undefined,
      page: 1,
    });
    onClearDateRange();
  }, [onClearDateRange, onFilterChange]);

  // Date range is the only dialog-only filter — Target/Status/Run by carry
  // their own inline badges in the toolbar.
  const modalFiltersCount = dateLabel ? 1 : 0;

  const filterDialogTrigger = (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="md">
      <MoreFiltersButton count={modalFiltersCount} />
      <Dialog title="More filters" description="Narrow down runs by when they started.">
        <DialogContent className="grid gap-4">
          <DateRangePicker
            label="Started"
            value={dateRange}
            onChange={onDateRangeChange}
            granularity="day"
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={handleResetFilters}>
            Reset filters
          </Button>
          <Button
            variant="primary"
            onPress={() => onOpenChange(false)}
            isDisabled={isDateRangeInvalid}
          >
            Done
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );

  return {
    filterDialogTrigger,
    activeFilterChips,
    hasActiveFilters,
    handleFilterRemove,
    handleResetFilters,
  };
}
